/**
 * Feature flags for Sparse Panel Overrides.
 *
 * There are two separate flags for a phased rollout:
 *
 * 1. SPARSE OVERRIDES (isSparseOverridesEnabled):
 *    When enabled, panel movements and config edits use sparse overrides
 *    (fractional indexing, override tracking) instead of setting isPanelsAuto=false.
 *    This is the "runtime" behavior flag.
 *
 * 2. SPARSE COMPRESSION (isSparseCompressionEnabled):
 *    When enabled, auto-generated panels are stripped from the spec during
 *    compression/save. This is the "persistence" behavior flag.
 *
 * Rollout phases:
 * - Phase 1 (Track Only): Overrides ON, Compression OFF
 *   Overrides are tracked and applied, but full panel state is still persisted.
 *   Safe rollback without data loss.
 *
 * - Phase 2 (Full Sparse): Overrides ON, Compression ON
 *   Auto-generated panels are stripped, only overrides persisted.
 *   Rollback would require workspace reset.
 *
 * This is a non-React utility that can be called from reducers and other
 * non-hook contexts. The Statsig flag values are cached by the
 * useRampFlagSparseOverrides and useRampFlagSparseCompression hooks
 * which should be called in workspace components.
 */

// Cached values from Statsig (set by hooks)
let cachedOverridesEnabled = false;
let cachedCompressionEnabled = false;

/**
 * Set the cached Statsig flag value for sparse overrides.
 * Called by useRampFlagSparseOverrides hook to make the value available
 * for synchronous access in reducers.
 */
export function setSparseOverridesEnabled(value: boolean): void {
  cachedOverridesEnabled = value;
}

/**
 * Set the cached Statsig flag value for sparse compression.
 * Called by useRampFlagSparseCompression hook to make the value available
 * for synchronous access in compression logic.
 */
export function setSparseCompressionEnabled(value: boolean): void {
  cachedCompressionEnabled = value;
}

/**
 * Check if sparse overrides feature is enabled (runtime behavior).
 * Controls: fractional indexing, override tracking, keeping isPanelsAuto=true.
 * Can be called from anywhere (reducers, hooks, utilities).
 */
export function isSparseOverridesEnabled(): boolean {
  return cachedOverridesEnabled;
}

/**
 * Check if sparse compression is enabled (persistence behavior).
 * Controls: stripping auto-generated panels during compression/save.
 * Can be called from anywhere (compression logic, web workers).
 */
export function isSparseCompressionEnabled(): boolean {
  return cachedCompressionEnabled;
}
